import { useState, useCallback, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { ParsedCsvResult, ParsedTransaction } from '@kitscope/shared'
import { fetchPortfolios, importTransactions, recalculateHoldings, fetchTickerMappings, type Portfolio } from '../lib/data'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB')
}

function Import() {
  const navigate = useNavigate()
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('')
  const [csvContent, setCsvContent] = useState('')
  const [result, setResult] = useState<ParsedCsvResult | null>(null)
  const [, _setLoading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ success: number; errors: string[] } | null>(null)
  const [tickerMappings, setTickerMappings] = useState<Record<string, string>>({})

  // Load portfolios and ticker mappings on mount
  useEffect(() => {
    loadPortfolios()
    loadTickerMappings()
  }, [])

  async function loadTickerMappings() {
    try {
      const mappings = await fetchTickerMappings()
      setTickerMappings(mappings)
    } catch (err) {
      console.warn('Failed to load ticker mappings:', err)
      // Continue without mappings - hardcoded ones will be used as fallback
    }
  }

  async function loadPortfolios() {
    try {
      const data = await fetchPortfolios()
      setPortfolios(data)
      if (data.length > 0) {
        setSelectedPortfolioId(data[0].id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolios')
    }
  }

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      setCsvContent(content)
      setResult(null)
      setError(null)
      setImportResult(null)
    }
    reader.readAsText(file)
  }, [])

  const handleParse = useCallback(async () => {
    if (!csvContent.trim()) {
      setError('Please select a CSV file')
      return
    }

    setParsing(true)
    setError(null)
    setResult(null)
    setImportResult(null)

    try {
      // Parse locally using the shared package, passing ticker mappings from database
      const { parseHLCSV } = await import('@kitscope/shared')
      const localResult = parseHLCSV(csvContent, new Set(), tickerMappings)
      setResult(localResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse CSV')
    } finally {
      setParsing(false)
    }
  }, [csvContent])

  const handleImport = useCallback(async () => {
    if (!result || !selectedPortfolioId) {
      setError('Please select a portfolio and parse a CSV file first')
      return
    }

    if (result.unmappedStocks.length > 0) {
      setError(`Cannot import: ${result.unmappedStocks.length} unmapped stock(s). Please map them first.`)
      return
    }

    setImporting(true)
    setError(null)

    try {
      // Convert parsed transactions to import format
      const transactionsToImport = result.transactions
        .filter((tx: ParsedTransaction): tx is ParsedTransaction & { ticker: string } => tx.ticker !== null)
        .map((tx) => ({
          ticker: tx.ticker,
          type: tx.type,
          tradeDate: tx.tradeDate,
          settleDate: tx.settleDate,
          quantity: tx.quantity,
          pricePence: tx.unitCostPence,
          valueGbp: tx.valueGbp,
          reference: tx.reference,
          description: tx.description,
        }))

      // Import transactions to Supabase
      const importRes = await importTransactions(selectedPortfolioId, transactionsToImport)
      setImportResult(importRes)

      // Recalculate holdings for each unique ticker
      const uniqueTickers = [...new Set(transactionsToImport.map(tx => tx.ticker))]
      for (const ticker of uniqueTickers) {
        try {
          await recalculateHoldings(selectedPortfolioId, ticker)
        } catch (err) {
          console.warn(`Failed to recalculate holding for ${ticker}:`, err)
        }
      }

      if (importRes.errors.length === 0) {
        // Success - clear and redirect after a delay
        setTimeout(() => {
          navigate('/')
        }, 2000)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import transactions')
    } finally {
      setImporting(false)
    }
  }, [result, selectedPortfolioId, navigate])

  return (
    <div className="app-container">
      {/* Back button and title */}
      <div className="flex items-center gap-2 mb-4">
        <Link to="/" className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold">Import CSV</h1>
      </div>

      {/* Portfolio selector */}
      <div className="card mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio</label>
        <select
          value={selectedPortfolioId}
          onChange={(e) => setSelectedPortfolioId(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          {portfolios.length === 0 ? (
            <option value="">No portfolios available</option>
          ) : (
            portfolios.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))
          )}
        </select>
        {portfolios.length === 0 && (
          <p className="text-sm text-amber-600 mt-2">
            Create a portfolio first before importing.
          </p>
        )}
      </div>

      {/* File upload */}
      <div className="card mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
        />
        {csvContent && (
          <p className="mt-2 text-xs text-gray-500">
            File loaded ({csvContent.length.toLocaleString()} characters)
          </p>
        )}
      </div>

      {/* Parse button */}
      <button
        onClick={handleParse}
        disabled={parsing || !csvContent || !selectedPortfolioId}
        className="btn-primary w-full mb-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {parsing ? 'Parsing...' : 'Parse CSV'}
      </button>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Import Success */}
      {importResult && (
        <div className={`border rounded-lg p-4 mb-6 ${importResult.errors.length === 0 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <p className={importResult.errors.length === 0 ? 'text-green-700' : 'text-amber-700'}>
            <span className="font-semibold">{importResult.success}</span> transactions imported successfully
          </p>
          {importResult.errors.length > 0 && (
            <div className="mt-2">
              <p className="text-amber-700 font-medium">{importResult.errors.length} errors:</p>
              <ul className="text-sm text-amber-700 mt-1 max-h-32 overflow-y-auto">
                {importResult.errors.map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
              </ul>
            </div>
          )}
          {importResult.errors.length === 0 && (
            <p className="text-green-600 text-sm mt-2">Redirecting to dashboard...</p>
          )}
        </div>
      )}

      {/* Results */}
      {result && !importResult && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="card bg-green-50 border-green-200">
            <h2 className="font-semibold text-green-900 mb-2">Preview</h2>
            <div className="space-y-1 text-sm">
              <p className="text-green-800">
                <span className="font-medium">{result.transactions.length}</span> transactions found
              </p>
              {result.unmappedStocks.length > 0 && (
                <p className="text-amber-700">
                  ⚠️ <span className="font-medium">{result.unmappedStocks.length}</span> unmapped stocks
                </p>
              )}
              {result.duplicateCount > 0 && (
                <p className="text-gray-600">
                  ⏭️ <span className="font-medium">{result.duplicateCount}</span> duplicates (will skip)
                </p>
              )}
              {result.errors.length > 0 && (
                <p className="text-red-600">
                  ❌ <span className="font-medium">{result.errors.length}</span> errors
                </p>
              )}
            </div>
          </div>

          {/* Unmapped stocks */}
          {result.unmappedStocks.length > 0 && (
            <div className="card border-amber-200">
              <h2 className="font-semibold text-amber-900 mb-2">⚠️ Unmapped Stocks</h2>
              <p className="text-sm text-amber-700 mb-2">
                These stocks need ticker mappings before importing. Run the setup script to seed mappings.
              </p>
              <ul className="space-y-1 text-sm">
                {result.unmappedStocks.map((name) => (
                  <li key={name} className="text-amber-800">• {name}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Transaction preview */}
          <div>
            <h2 className="font-semibold text-gray-900 mb-2">Transaction Preview</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {result.transactions.slice(0, 10).map((tx: ParsedTransaction, i) => (
                <div key={i} className="card py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">{formatDate(tx.tradeDate)}</span>
                    <span className={`font-medium ${tx.valueGbp >= 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {tx.valueGbp >= 0 ? '+' : ''}{formatCurrency(tx.valueGbp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100">
                      {tx.type}
                    </span>
                    {tx.ticker && (
                      <span className="font-mono text-gray-700">{tx.ticker}</span>
                    )}
                    {tx.quantity && (
                      <span className="text-gray-500">{tx.quantity} units</span>
                    )}
                  </div>
                </div>
              ))}
              {result.transactions.length > 10 && (
                <p className="text-center text-sm text-gray-500 py-2">
                  ...and {result.transactions.length - 10} more
                </p>
              )}
            </div>
          </div>

          {/* Import button */}
          <button
            onClick={handleImport}
            disabled={importing || result.unmappedStocks.length > 0}
            className="btn-primary w-full sticky bottom-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {importing ? 'Importing...' : `Import ${result.transactions.length} Transactions`}
          </button>
        </div>
      )}
    </div>
  )
}

export default Import
